import React from "react";
import {
  Bell,
  Settings,
  Database,
  Cloud,
  ChevronRight,
  ArrowRight,
  Edit3,
  Ban,
  History,
  MessageSquare,
  Send,
  BarChart3,
  CheckCircle2,
  Search,
  Workflow,
  HelpCircle,
  Headphones,
  Calculator,
  SlidersHorizontal,
  Boxes,
} from "lucide-react";

const TransactionReviewResolutionPage = () => {
  return (
   <div className="bg-gray-50 min-h-screen p-6 font-display text-slate-900 min-h-screen flex flex-col">
      {/* Main */}
    <main className="flex-1 px-6 md:px-10 py-8 max-w-[1440px] mx-auto w-full">
      
      <div className="bg-gray-50 min-h-screen p-6">
        {/* Content */}
       
          {/* Page Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                <span>Discrepancies</span>
                <ChevronRight size={15} />
                <span className="text-slate-900 font-semibold">
                  Review & Resolution
                </span>
              </div>

              <h1 className="text-2xl font-black tracking-tight">
                Transaction ID: TXN-99210-XC
              </h1>

              <p className="text-slate-500 mt-2">
                Investigating variance in inter-company clearing
                accounts.
              </p>
            </div>

            <div className="flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full font-semibold">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              Pending Review
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-12 gap-8">
            {/* LEFT */}
            <div className="col-span-12 xl:col-span-8 space-y-8">
              {/* Comparison */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                  <h2 className="text-xl font-bold">
                    Comparison Matrix
                  </h2>

                  <div className="flex items-center gap-5 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-red-500 rounded-full" />
                      Mismatch
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-slate-300 rounded-full" />
                      Matched
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-px bg-slate-200">
                  {/* Source */}
                  <div className="bg-white p-6">
                    <div className="flex items-center gap-2 text-blue-700 font-bold mb-6">
                      <Database size={20} />
                      Source: Internal Ledger
                    </div>

                    <div className="space-y-4">
                      <Field
                        label="Entity Name"
                        value="Vertex Logistics NV"
                      />

                      <Field
                        mismatch
                        label="Tax ID / VAT"
                        value="VAT-9921-LXS"
                      />

                      <Field
                        label="Posting Date"
                        value="Oct 12, 2023"
                      />

                      <Field
                        mismatch
                        amount
                        label="Net Amount (USD)"
                        value="42,550.00"
                      />
                    </div>
                  </div>

                  {/* Target */}
                  <div className="bg-white p-6">
                    <div className="flex items-center gap-2 text-violet-700 font-bold mb-6">
                      <Cloud size={20} />
                      Target: Vendor Portal
                    </div>

                    <div className="space-y-4">
                      <Field
                        label="Entity Name"
                        value="Vertex Logistics NV"
                      />

                      <Field
                        mismatch
                        label="Tax ID / VAT"
                        value="VAT-9921-ZXZ"
                      />

                      <Field
                        label="Posting Date"
                        value="Oct 12, 2023"
                      />

                      <Field
                        mismatch
                        amount
                        label="Net Amount (USD)"
                        value="42,675.50"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Diagnostics */}
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <BarChart3 className="text-blue-700" size={22} />
                  Root Cause Diagnostics
                </h2>

                <div className="grid md:grid-cols-2 gap-5">
                  <DiagnosticCard
                    icon={<Settings size={18} />}
                    title="Input Parameters"
                    description="The VAT identifier mapping has drifted in the vendor portal due to an API update."
                    tag="MAPPING DISCREPANCY"
                    danger
                  />

                  <DiagnosticCard
                    icon={<SlidersHorizontal size={18} />}
                    title="Custom Parameters"
                    description="Variance of $125.50 exceeds standard auto-reconcile threshold."
                    tag="TOLERANCE EXCEEDED"
                  />

                  <DiagnosticCard
                    icon={<Calculator size={18} />}
                    title="Formula Outcome"
                    description="Tax calculated as 19% vs 19.5% on shipping line items."
                    tag="CALCULATED TAX DIFF"
                  />

                  <DiagnosticCard
                    icon={<Boxes size={18} />}
                    title="Master Data"
                    description="Primary Vendor ID mismatch between systems."
                    tag="CRITICAL CONFLICT"
                    danger
                    critical
                  />
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="col-span-12 xl:col-span-4 space-y-8">
              {/* Actions */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-xl font-bold mb-6">
                  Resolution Action
                </h2>

                <div className="space-y-4">
                  <ActionButton
                    primary
                    icon={<ArrowRight size={18} />}
                    label="Accept Source Values"
                  />

                  <ActionButton
                    secondary
                    icon={<ArrowRight size={18} />}
                    label="Accept Target Values"
                  />

                  <ActionButton
                    icon={<Edit3 size={18} />}
                    label="Manual Adjustment"
                  />

                  <button className="w-full py-3 rounded-2xl flex items-center justify-center gap-2 text-slate-500 hover:bg-red-50 hover:text-red-600 transition">
                    <Ban size={18} />
                    Ignore Discrepancy
                  </button>
                </div>
              </div>

              {/* History */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-200">
                <h2 className="text-lg font-bold flex items-center gap-2 mb-6">
                  <History size={18} />
                  Audit History
                </h2>

                <div className="border-l-2 border-slate-200 ml-2 pl-6 space-y-6 mb-8">
                  <TimelineItem
                    title="Oct 14, 09:12 AM"
                    desc="System flagged discrepancy (Auto-scan #441)"
                    active
                  />

                  <TimelineItem
                    title="Oct 14, 11:30 AM"
                    desc="J. Miller assigned for manual review"
                  />
                </div>

                <h2 className="text-lg font-bold flex items-center gap-2 mb-4">
                  <MessageSquare size={18} />
                  Analyst Notes
                </h2>

                <div className="bg-slate-100 rounded-2xl p-4 text-sm mb-4">
                  <div className="flex justify-between mb-2">
                    <span className="font-bold text-blue-700">
                      J. Miller
                    </span>

                    <span className="text-slate-500">
                      1 hr ago
                    </span>
                  </div>

                  Vendor portal was down for maintenance last
                  night, might have cached incorrect VAT strings.
                </div>

                <div className="relative">
                  <textarea
                    rows={4}
                    placeholder="Add an internal note..."
                    className="w-full rounded-2xl bg-slate-100 border-none focus:ring-2 focus:ring-blue-300 p-4 pr-14 resize-none"
                  />

                  <button className="absolute bottom-3 right-3 bg-blue-700 text-white p-2 rounded-xl">
                    <Send size={16} />
                  </button>
                </div>
              </div>

              {/* Confidence */}
              <div className="relative h-36 rounded-3xl overflow-hidden shadow-sm">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop"
                  alt="analytics"
                  className="w-full h-full object-cover"
                />

                <div className="absolute inset-0 bg-blue-900/50 backdrop-blur-sm flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="text-sm uppercase tracking-widest">
                      System Confidence
                    </div>

                    <div className="text-4xl font-black mt-1">
                      84%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
       
      </div>
      </main>
    </div>
  );
};

const SidebarItem = ({ icon, label, active }) => (
  <button
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition font-medium ${
      active
        ? "bg-blue-50 text-blue-700"
        : "text-slate-600 hover:bg-slate-100"
    }`}
  >
    {icon}
    {label}
  </button>
);


const SmallLink = ({ icon, label }) => (
  <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-blue-700 transition">
    {icon}
    {label}
  </button>
);

const Field = ({ label, value, mismatch, amount }) => (
  <div
    className={`rounded-2xl p-4 border ${
      mismatch
        ? "bg-red-50 border-red-200"
        : "bg-slate-50 border-slate-100"
    }`}
  >
    <div
      className={`text-xs uppercase font-bold mb-1 ${
        mismatch ? "text-red-600" : "text-slate-500"
      }`}
    >
      {label}
    </div>

    <div
      className={`${
        amount ? "text-3xl font-black tracking-tight" : "font-semibold"
      } ${mismatch ? "text-red-700" : "text-slate-900"}`}
    >
      {value}
    </div>
  </div>
);

const DiagnosticCard = ({
  icon,
  title,
  description,
  tag,
  danger,
  critical,
}) => (
  <div
    className={`rounded-2xl p-5 ${
      critical
        ? "border-l-4 border-red-500 bg-red-50"
        : "bg-slate-50"
    }`}
  >
    <div className="flex items-center gap-2 font-bold mb-3">
      <span className={danger ? "text-red-600" : "text-slate-600"}>
        {icon}
      </span>

      {title}
    </div>

    <p className="text-sm text-slate-600 leading-relaxed">
      {description}
    </p>

    <span
      className={`inline-block mt-4 text-[10px] uppercase font-bold px-3 py-1 rounded-full ${
        danger
          ? "bg-red-100 text-red-700"
          : "bg-violet-100 text-violet-700"
      }`}
    >
      {tag}
    </span>
  </div>
);

const ActionButton = ({
  label,
  icon,
  primary,
  secondary,
}) => (
  <button
    className={`w-full rounded-2xl py-4 px-4 flex items-center justify-between font-bold transition ${
      primary
        ? "bg-blue-700 hover:bg-blue-800 text-white"
        : secondary
        ? "bg-violet-100 hover:bg-violet-200 text-violet-700"
        : "bg-slate-100 hover:bg-slate-200 text-slate-800"
    }`}
  >
    <span>{label}</span>
    {icon}
  </button>
);

const TimelineItem = ({ title, desc, active }) => (
  <div className="relative">
    <div
      className={`absolute -left-[33px] top-1 w-3 h-3 rounded-full ${
        active ? "bg-blue-700" : "bg-slate-300"
      }`}
    />

    <div className="font-bold text-sm">{title}</div>

    <div className="text-sm text-slate-500 mt-1">
      {desc}
    </div>
  </div>
);

export default TransactionReviewResolutionPage;